import './bookDemoForm.scss';
import { components } from 'common';
import { useDebounceFn } from 'ahooks';
import React, { useState, useEffect } from 'react';
import { $document } from '@umu/umu-dom';
import { contactSales } from '@umu/umu-api';
import { lang, _lang, isSystem } from '@umu/ukit-lang';
import { localStorageHelper, STORAGE_KEY } from '@umu/umu-storage';
import { bizComponents, bizServices, IChangeCallbackArgs } from 'businessCommon';
import { IOutputContactSales } from '@umu/umu-api/src/services/contractSales/contractSales';
import { ChannelType, FormType, IProp, OptionItemType, ParamsType } from './bookDemoFormInterface';
import {
    getDefaultState,
    staffCountOptionList,
    howHearAboutUsOptionsList,
    excludeEmailExt,
} from './bookDemoFormDataFlow';
import { Select, Checkbox } from '@umu/umu-design';
import { CheckboxChangeEvent } from '@umu/umu-design/es/components/Checkbox';

const { MailInput, PhoneInput } = bizComponents;
const { validateMail, validatePhone } = bizServices;
const { If, Button, CustomDialog, Textarea } = components;
const CHANNEL_TYPE_BOOK_DEMO = 8;

const scrollIntoView = (platform: IProp['platform']) => {
    const errorTipNode = document.querySelector('.form-tip.error');
    if (errorTipNode) {
        const formRowNode = errorTipNode.parentElement!;
        formRowNode.scrollIntoView();
        const bufferDistance = 2;
        let secondScroll = bufferDistance;
        if (platform === 'twww') {
            const headerHeight = 56;
            secondScroll = headerHeight;
        }

        const scrolledY = window.scrollY;
        if (scrolledY) {
            window.scroll(0, scrolledY - secondScroll);
        }
    }
};

function Form(props: IProp) {
    const [formState, SetFormState] = useState(getDefaultState);
    localStorageHelper.set(STORAGE_KEY.AREA_CODE, '');
    const lastNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.lastName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const firstNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.firstName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const enterpriseNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.enterpriseName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const mailChangeCallback = (data: string) => {
        SetFormState((pre) => {
            pre.email = data;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const positionNameChangeCallback = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = (e.currentTarget.value || '').trim();
        SetFormState((pre) => {
            pre.positionName = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const phoneChangeCallback = (data: IChangeCallbackArgs) => {
        SetFormState((pre) => {
            pre.codePosition = data.selectedCountryPosition + '';
            pre.areaCode = data.countryCode + '';
            pre.phone = data.phoneNumber + '';
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const descriptionChangeCallback = (value: string) => {
        value = value.trim();
        SetFormState((pre) => {
            pre.description = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const staffCountChangeCallback = (item: OptionItemType) => {
        const value = item.id;
        SetFormState((pre) => {
            pre.staffCount = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const receiveSubscribeChangeCallback = (e: CheckboxChangeEvent) => {
        const {
            target: { checked },
        } = e;
        SetFormState((pre) => {
            pre.receiveSubscribe = checked;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const howHearAboutUsChangeCallback = (item: OptionItemType) => {
        const value = item.id;
        SetFormState((pre) => {
            pre.howHearAboutUs = value;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };
    const checkLastName = (lastName: string) => {
        const result = (lastName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showLastName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkFirstName = (firstName: string) => {
        const result = (firstName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showFirstName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkPhone = (phone: string) => {
        const result = validatePhone(phone);
        SetFormState((pre) => {
            pre.showPhoneMsg = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkMail = (email: string) => {
        const result = validateMail(email);
        SetFormState((pre) => {
            pre.showMailMsg = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkMailOfExt = (email: string) => {
        const result = !excludeEmailExt.some((item) => {
            return email.includes(item);
        });

        SetFormState((pre) => {
            pre.showMailMsgExt = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkEnterpriseName = (enterpriseName: string) => {
        const result = (enterpriseName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showEnterpriseName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const checkPositionName = (positionName: string) => {
        const result = (positionName || '').trim() !== '';
        SetFormState((pre) => {
            pre.showPositionName = !result;
            const newState = Object.assign({}, pre);
            return newState;
        });
        return result;
    };
    const resetState = () => {
        const eles = $document.querySelectorAll('input');
        eles.forEach((ele) => {
            if (ele.type === 'text' || ele.type === 'email' || ele.type === 'tel') {
                ele.value = '';
            }
        });
        document.querySelectorAll('textarea').forEach((ele) => {
            ele.value = '';
        });
        SetFormState((pre) => {
            pre = getDefaultState;
            const newState = Object.assign({}, pre);
            return newState;
        });
    };

    const submitFormSuccess = () => {
        switch (props.type) {
            case FormType.contractSales:
            case FormType.bookDemo:
                CustomDialog({
                    className: 'cotact-sales-dialog',
                    canClose: true,
                    width: '4rem',
                    childProps: {
                        resetState,
                    },
                    contentComponent: props.contentComponent,
                });
                return;
            case FormType.brandGuide:
                if (props.downloadUrl) {
                    window.open(props.downloadUrl, '_blank');
                }
                resetState();
                props.changeHasSubmitted?.(true);
                return;
            case FormType.whitePaper:
                if (props.downloadUrl) {
                    window.open(props.downloadUrl, '_blank');
                }
                resetState();
                props.changeHasSubmitted?.(true);
        }
    };

    const setPropertyOfChannelType = (params: ParamsType) => {
        switch (props.type) {
            case FormType.brandGuide:
                params.channel_type = ChannelType[props.type];
            case FormType.whitePaper:
                params.channel_type = ChannelType[props.type];
        }
    };

    const submitForm = useDebounceFn(
        () => {
            const {
                lastName,
                firstName,
                phone,
                email,
                enterpriseName,
                positionName,
                areaCode,
                description,
                department,
                staffCount,
                howHearAboutUs,
                receiveSubscribe,
            } = formState;
            if (!checkLastName(lastName)) {
                return;
            }
            if (!checkFirstName(firstName)) {
                return;
            }
            if (!checkPhone(phone)) {
                return;
            }
            if (!checkMail(email)) {
                return;
            }
            if (!checkMailOfExt(email)) {
                return;
            }
            if (!checkEnterpriseName(enterpriseName)) {
                return;
            }
            if (!checkPositionName(positionName)) {
                return;
            }

            const params: ParamsType = {
                first_name: firstName,
                last_name: lastName,
                phone: `${areaCode}-${phone}`,
                email,
                enterprise_name: enterpriseName,
                position_name: positionName,
                source: props.referUrl,
                description,
                department,
                staff_count: staffCount,
                how_hear_about_us: howHearAboutUs,
                receive_subscribe: Number(receiveSubscribe),
            };

            if (isSystem('EM') || isSystem('EN')) {
                params.channel_type = CHANNEL_TYPE_BOOK_DEMO;
            }

            setPropertyOfChannelType(params);
            contactSales(params).then((res: IOutputContactSales) => {
                if (res.data) {
                    submitFormSuccess();
                }
            });
            // 荟聚 表单埋点
            if (isSystem('CN')) {
                try {
                    const getCookie = (name: string) => {
                        const reg = new RegExp(`(^| )${name}=([^;]*)(;|$)`);
                        const arr = document.cookie.match(reg);
                        return arr ? arr[2] : '';
                    };
                    const utma = getCookie('c__utma');
                    const utmb = getCookie('c__utmb');
                    fetch('https://host.huiju.cool/formdata/get/00b4d4acede043cba1ea117561b6398b', {
                        method: 'GET',
                    })
                        .then((response) => response.json())
                        .then((res: any) => {
                            if (res.token) {
                                const paramsData: any = {
                                    name: `${params.last_name || ''}${params.first_name || ''}`,
                                    mobile: phone,
                                    email: params.email,
                                    company: params.enterprise_name,
                                    title: params.position_name,
                                    attr8: params.description,
                                };
                                let reqData = '';
                                Object.keys(paramsData).forEach((item) => {
                                    reqData = `${reqData ? `${reqData}&` : ''}${item}=${paramsData[item]}`;
                                });
                                reqData = `${reqData}&utma=${utma}&utmb=${utmb}&cltoken=${res.token}`;
                                fetch('https://host.huiju.cool/form/00b4d4acede043cba1ea117561b6398b', {
                                    body: reqData,
                                    method: 'POST',
                                });
                            }
                        });
                } catch (error) {}
            }
        },
        { wait: 3000, leading: true, trailing: false },
    );

    const lastNameElement = (
        <div
            key={'lastNameElement'}
            className={formState.showLastName ? 'input name last-name error' : 'input name last-name'}
        >
            <If condition={props.platform === 'twww'}>
                <div className="lable-title">
                    {lang('姓')}
                    <i className="icon icon-required" />
                </div>
            </If>
            <input
                type="text"
                placeholder={lang('请填写您的姓')}
                defaultValue={formState.lastName}
                onChange={lastNameChangeCallback}
            />
            <If condition={formState.showLastName}>
                <p className="form-tip error">{lang('请填写您的姓')}</p>
            </If>
        </div>
    );

    const firstNameElement = (
        <div key={'firstNameElement'} className={formState.showFirstName ? 'input name error' : 'input name'}>
            <If condition={props.platform === 'twww'}>
                <div className="lable-title">
                    {lang('名')}
                    <i className="icon icon-required" />
                </div>
            </If>
            <input
                type="text"
                defaultValue={formState.firstName}
                onChange={firstNameChangeCallback}
                placeholder={lang('请填写您的名')}
            />
            <If condition={formState.showFirstName}>
                <p className="form-tip error">{lang('请填写您的名')}</p>
            </If>
        </div>
    );

    const getUserNameForms = () => {
        if (_lang('en')) {
            return [firstNameElement, lastNameElement];
        }

        return [lastNameElement, firstNameElement];
    };

    const renderButton = () => {
        switch (props.type) {
            case FormType.bookDemo:
                return (
                    <Button className="contract-sales-btn" onClick={() => submitForm.run()}>
                        {lang('预约演示')}
                    </Button>
                );
            case FormType.contractSales:
                return (
                    <Button className="contract-sales-btn" onClick={() => submitForm.run()}>
                        {lang('联系 UMU 销售')}
                    </Button>
                );
            case FormType.brandGuide:
                return (
                    <Button className="white-paper-btn" onClick={() => submitForm.run()}>
                        {lang('下载 UMU 品牌素材')}
                    </Button>
                );
            case FormType.whitePaper:
                return (
                    <Button className="white-paper-btn" onClick={() => submitForm.run()}>
                        {lang('马上获取')}
                    </Button>
                );
        }
    };

    useEffect(() => {
        document.body.classList.add('form-body');
        return () => {
            document.body.classList.remove('form-body');
        };
    }, []);

    // 滚动
    useEffect(() => {
        const hasError =
            formState.showLastName ||
            formState.showFirstName ||
            formState.showPhoneMsg ||
            formState.showMailMsg ||
            formState.showEnterpriseName ||
            formState.showPositionName;

        if (hasError) {
            scrollIntoView(props.platform);
        }
    }, [formState, props.platform]);

    return (
        <>
            <div className="form-row form-user-row">{getUserNameForms()}</div>
            <div className={formState.showPhoneMsg ? 'form-row error' : 'form-row'}>
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">
                        {lang('手机号码')}
                        <i className="icon icon-required" />
                    </div>
                </If>
                <PhoneInput
                    initialPhone={formState.phone}
                    initialValue={formState.areaCode}
                    initialPosition={formState.codePosition}
                    changeCallback={phoneChangeCallback}
                />
                <If condition={formState.showPhoneMsg}>
                    <p className="form-tip error">{lang('请填写正确的手机号')}</p>
                </If>
            </div>
            <div
                className={
                    formState.showMailMsg || formState.showMailMsgExt
                        ? 'form-row mail-input-wrap error'
                        : 'form-row mail-input-wrap'
                }
            >
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">
                        {lang('工作邮箱')}
                        <i className="icon icon-required" />
                    </div>
                </If>
                <MailInput
                    initialVal={formState.email}
                    isShowSuggestion={false}
                    changeCallback={mailChangeCallback}
                    placeholder={lang('you@example.com')}
                />
                <If condition={formState.showMailMsg}>
                    <p className="form-tip error">{lang('请填写有效的工作邮箱')}</p>
                </If>
                <If condition={formState.showMailMsgExt}>
                    <p className="form-tip error">{lang('请填写您的工作邮箱')}</p>
                </If>
            </div>
            <div className={formState.showEnterpriseName ? 'form-row error' : 'form-row'}>
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">
                        {lang('企业或单位名称')}
                        <i className="icon icon-required" />
                    </div>
                </If>
                <div className="input">
                    <input
                        type="text"
                        defaultValue={formState.enterpriseName}
                        onChange={enterpriseNameChangeCallback}
                        placeholder={lang('请填写企业或单位名称')}
                    />
                </div>
                <If condition={formState.showEnterpriseName}>
                    <p className="form-tip error">{lang('请填写企业或单位名称')}</p>
                </If>
            </div>
            <div className="form-row">
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">{lang('公司规模')}</div>
                </If>
                <div className="select">
                    <Select
                        selected={formState.staffCount}
                        list={staffCountOptionList}
                        placeholder={lang('请选择公司规模…')}
                        onSelect={staffCountChangeCallback}
                    />
                </div>
            </div>
            <div className={`${formState.showPositionName ? 'form-row error' : 'form-row'}`}>
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">
                        {lang('职位')}
                        <i className="icon icon-required" />
                    </div>
                </If>
                <div className="input">
                    <input
                        type="text"
                        defaultValue={formState.positionName}
                        onChange={positionNameChangeCallback}
                        placeholder={lang('请填写您的职位')}
                    />
                </div>
                <If condition={formState.showPositionName}>
                    <p className="form-tip error">{lang('请填写您的职位')}</p>
                </If>
            </div>
            <div className="form-row">
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">{lang('您是如何听说我们的？')}</div>
                </If>
                <div className="select">
                    <Select
                        selected={formState.howHearAboutUs}
                        list={howHearAboutUsOptionsList}
                        placeholder={lang('您是如何听说我们的？')}
                        onSelect={howHearAboutUsChangeCallback}
                    />
                </div>
            </div>
            <div className="form-row">
                <If condition={props.platform === 'twww'}>
                    <div className="lable-title">{lang('留言')}</div>
                </If>
                <div className="input">
                    <Textarea
                        showCharCount={true}
                        value={formState.description}
                        placeholder={
                            lang('您希望通过UMU解决什么样的挑战？') + '\r' + lang('您想在演示中了解哪些方面的内容？')
                        }
                        onChange={descriptionChangeCallback}
                        maxLength={_lang('cn') || _lang('jp') ? 1000 : 2000}
                    />
                </div>
            </div>
            <div className="form-row">
                <Checkbox checked={formState.receiveSubscribe} onChange={(e) => receiveSubscribeChangeCallback(e)}>
                    {lang('接收来自UMU的订阅材料')}
                </Checkbox>
            </div>
            <div className="contract-sales-footer">{renderButton()}</div>
        </>
    );
}

export default Form;
